import React, { useState } from 'react';
import { isKnownAvatar, avatarArt } from '../data/cosmeticsCatalog.js';
import AvatarFrame from './AvatarFrame.jsx';

// Web avatar renderer. Shows the equipped avatar art (light/dark variant picked
// by the OS color scheme via <picture>), falling back to the initial-circle the
// web has always used when no avatar is equipped, the id is unknown, or the
// image fails to load. Surfaced in the Profile identity, group rankings, and
// (Phase 3) chat author rows.
//
// When `frameId` is set, an AvatarFrame SVG is overlaid on top (the iOS
// FrameStyle engine, ported). `frameAnimated` plays the animated styles — pass
// true only on a single hero (profile/member-sheet), not in scrolling lists.
export default function Avatar({ avatarId, name, size = 40, ring = false, frameId = null, frameAnimated = false }) {
  const [failed, setFailed] = useState(false);
  const initial = ((name || '?').trim().charAt(0) || '?').toUpperCase();
  const base = {
    width: size,
    height: size,
    borderRadius: '50%',
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    ...(ring ? { boxShadow: '0 0 0 2px var(--gold), 0 0 10px rgba(212,168,75,0.5)' } : null),
  };

  let visual;
  if (isKnownAvatar(avatarId) && !failed) {
    visual = (
      <span style={base}>
        <picture>
          <source srcSet={avatarArt(avatarId, 'dark')} media="(prefers-color-scheme: dark)" />
          <img
            src={avatarArt(avatarId, 'light')}
            alt={name || 'Avatar'}
            width={size}
            height={size}
            loading="lazy"
            onError={() => setFailed(true)}
            style={{ width: size, height: size, objectFit: 'cover', display: 'block' }}
          />
        </picture>
      </span>
    );
  } else {
    // Initial-circle fallback.
    visual = (
      <span
        style={{
          ...base,
          background: 'rgba(212,168,75,0.15)',
          color: 'var(--accent)',
          fontWeight: 800,
          fontSize: Math.round(size * 0.42),
        }}
        aria-label={name || 'Avatar'}
      >
        {initial}
      </span>
    );
  }

  if (!frameId) return visual;

  // Frame overlay sits on top of the avatar; the wrapper keeps the avatar's
  // layout footprint (size × size) while the frame's ornaments draw outside it.
  return (
    <span style={{ position: 'relative', width: size, height: size, display: 'inline-flex', flexShrink: 0 }}>
      {visual}
      <AvatarFrame frameId={frameId} size={size} animated={frameAnimated} />
    </span>
  );
}
